import type { Platform } from "../platform/Platform";

/** "⌘" on macOS, "Ctrl" elsewhere: `platform.shortcuts.modifier`. */
type ShortcutModifierLabel = Platform["shortcuts"]["modifier"];
export type ShortcutPlatform = "mac" | "windows";
export type ShortcutTarget = ShortcutModifierLabel | ShortcutPlatform;

function detectShortcutPlatform(): ShortcutPlatform {
    return typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent) ? "mac" : "windows";
}

const MODIFIERS = ["Mod", "Alt", "Shift"] as const;
type ModifierToken = (typeof MODIFIERS)[number];

const MAC_GLYPHS: Record<string, string> = {
    Mod: "⌘",
    Alt: "⌥",
    Shift: "⇧",
    Enter: "↵",
    Left: "←",
    Right: "→",
};

const OTHER_GLYPHS: Record<string, string> = {
    Mod: "Ctrl",
    Alt: "Alt",
    Shift: "Shift",
    Enter: "Enter",
    Left: "←",
    Right: "→",
};

/** Apple orders modifier glyphs ⌥ ⇧ ⌘; Windows spells Ctrl+Alt+Shift. */
const MAC_ORDER: readonly ModifierToken[] = ["Alt", "Shift", "Mod"];
const OTHER_ORDER: readonly ModifierToken[] = ["Mod", "Alt", "Shift"];

function isModifier(token: string): token is ModifierToken {
    return (MODIFIERS as readonly string[]).includes(token);
}

/**
 * Formats a "+"-joined spec ("Mod+F", "Alt+Mod+Enter") for the platform modifier.
 * Modifier order in the spec does not matter; the output uses the platform's order.
 * macOS concatenates glyphs ("⌥⌘↵"); every other platform joins with "+" ("Ctrl+Alt+Enter").
 */
export function formatShortcut(
    spec: string,
    target: ShortcutTarget = detectShortcutPlatform(),
): string {
    const mac = target === "⌘" || target === "mac";
    const glyphs = mac ? MAC_GLYPHS : OTHER_GLYPHS;
    const tokens = spec.split("+");
    const key = tokens.find((token) => !isModifier(token)) ?? "";
    const ordered = (mac ? MAC_ORDER : OTHER_ORDER).filter((token) => tokens.includes(token));
    const parts = [...ordered.map((token) => glyphs[token]), glyphs[key] ?? key];
    return mac ? parts.join("") : parts.join("+");
}

/** The local-device noun: "This Mac" beside a ⌘, "This PC" beside a Ctrl. */
export function localDeviceLabel(target: ShortcutTarget = detectShortcutPlatform()): string {
    return target === "⌘" || target === "mac" ? "This Mac" : "This PC";
}
